import { useMutation, useQuery } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import { Alert, Box, Button, Chip, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { GetAccumulationAreaQuery, WasteAccumulationAreaType } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import pluralize from "pluralize";
import { ReactNode, useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  NO_VALUE_PLACEHOLDER,
  formatContainerDate,
} from "routes/Customer/Waste/Containers/containerDisplay";
import { FieldValue, SectionCard } from "routes/Customer/Waste/Containers/containerSections";
import { WasteContainerDrawer } from "routes/Customer/Waste/Containers/WasteContainerDrawer";
import { FacilityContainersGrid } from "routes/Customer/Waste/Containers/WasteContainersList";

import { WastePageFrame } from "../components/WastePageFrame";
import { AccumulationAreaDialog } from "./AccumulationAreaDialog";
import { DELETE_ACCUMULATION_AREA_MUTATION, GET_ACCUMULATION_AREA_QUERY } from "./api";
import { formatGallons, SatelliteVolumeGauge, STATUS_COLOR, STATUS_LABEL } from "./AreaCard";
import { displayAreaName } from "./areaDisplay";

type AccumulationAreaDetailData = NonNullable<GetAccumulationAreaQuery["accumulationArea"]>;

// Stable reference: OmnisearchDataGridV2 expects a memoized commandButtons
// array, and this section doesn't need any (Edit/Archive live in the header).
const NO_COMMAND_BUTTONS: ReactNode[] = [];

/**
 * The Details card is half the page, so its fields run two columns rather than
 * the shared default's three: prose on the left at 2/3, fixed-width facts (a
 * coordinate pair, a date) on the right at 1/3. An equal half left the short
 * values swimming.
 */
const WIDE_FIELD = { xs: 12, sm: 8 } as const;
const NARROW_FIELD = { xs: 12, sm: 4 } as const;

/**
 * Latitude and longitude as one field.
 *
 * The edit dialog keeps them as two inputs (as does `StorageLocationLatLong`),
 * because they are typed separately — but read back they are only meaningful as
 * a pair, and one field leaves room for another fact in a half-width card.
 *
 * Only the halves that are set are joined: the placeholder is the empty string,
 * so pairing a lone latitude with it would render a dangling "41.2, ".
 */
function coordinatesLabel(area: AccumulationAreaDetailData): string {
  const parts = [area.latitude, area.longitude].filter((part) => part != null);
  if (parts.length === 0) {
    return NO_VALUE_PLACEHOLDER;
  }
  return parts.join(", ");
}

function DetailsCard({ area }: { area: AccumulationAreaDetailData }) {
  const isCentral = area.type === WasteAccumulationAreaType.Central;

  return (
    <SectionCard surface="paper" title="Details">
      {/* Read across then down: description beside its location, then the
          container count beside the deadline that count drives. */}
      <Grid container spacing={2}>
        <FieldValue size={WIDE_FIELD} label="Description">
          {area.description ?? NO_VALUE_PLACEHOLDER}
        </FieldValue>
        <FieldValue size={NARROW_FIELD} label="Lat/Long">
          {coordinatesLabel(area)}
        </FieldValue>
        <FieldValue size={WIDE_FIELD} label="Containers">
          {area.containerCount}
        </FieldValue>
        <FieldValue size={NARROW_FIELD} label="Nearest container deadline">
          {area.nearestDeadlineDate
            ? formatContainerDate(area.nearestDeadlineDate)
            : NO_VALUE_PLACEHOLDER}
        </FieldValue>
        {/* Central only: a satellite area's containers have not been moved to
            central yet by definition, so the field would always be empty. */}
        {isCentral && (
          <FieldValue size={WIDE_FIELD} label="Oldest moved to central">
            {area.oldestMovedToCentralDate
              ? formatContainerDate(area.oldestMovedToCentralDate)
              : NO_VALUE_PLACEHOLDER}
          </FieldValue>
        )}
      </Grid>
    </SectionCard>
  );
}

function CentralVolumeSummary({ area }: { area: AccumulationAreaDetailData }) {
  const totalOnSite = area.volume.nonAcute.total + area.volume.acute.totalVolume;
  return (
    <Stack spacing={1}>
      <Typography variant="body2">{pluralize("container", area.containerCount, true)}</Typography>
      <Typography variant="body2">{formatGallons(totalOnSite)} gal on site</Typography>
      <Typography variant="body2" color={`${STATUS_COLOR[area.volume.status]}.main`}>
        {STATUS_LABEL[area.volume.status]}
      </Typography>
    </Stack>
  );
}

/** Title, the chips that qualify it, and the page actions. */
function DetailHeader({
  area,
  isArchived,
  onEdit,
  onArchive,
}: {
  area: AccumulationAreaDetailData;
  isArchived: boolean;
  onEdit: () => void;
  onArchive: () => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={2}
    >
      <Box>
        <Typography variant="h5">{displayAreaName(area.name)}</Typography>
        {/* Under the title rather than beside it: the chips qualify the name,
            and inline they pushed the actions around as the name got longer. */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Chip
            label={area.type === WasteAccumulationAreaType.Central ? "Central" : "Satellite"}
            size="small"
            variant="outlined"
          />
          <Chip
            label={STATUS_LABEL[area.volume.status]}
            size="small"
            color={STATUS_COLOR[area.volume.status]}
          />
          {isArchived && <Chip label="Archived" size="small" variant="outlined" />}
        </Stack>
      </Box>
      {!isArchived && (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<Edit />} onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<Inventory2Outlined />}
            onClick={onArchive}
          >
            Archive
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Archiving the area, with the cache eviction that makes it stick.
 *
 * The mutation archives an area that has held containers and deletes one that
 * never has, which is why the success copy reads off the response rather than
 * assuming either.
 */
function useArchiveArea(area: AccumulationAreaDetailData) {
  const alerts = useAlerts();
  const navigate = useNavigate();
  const { tenantId } = useTenant();

  const [deleteArea, { loading: isDeleting }] = useMutation(DELETE_ACCUMULATION_AREA_MUTATION, {
    // The areas list lives on a sibling route, so it's unmounted (and its
    // refetchQueries no-op) while this mutation is in flight. Evicting the
    // area from the cache is what actually removes it before the list
    // remounts — this also covers the archive case, since archived areas
    // are filtered out server-side and would otherwise still show from the
    // stale cached list.
    update: (cache) => {
      cache.evict({ id: cache.identify({ __typename: "WasteAccumulationArea", id: area.id }) });
      cache.gc();
    },
  });

  const archive = () =>
    deleteArea({
      variables: { id: area.id },
      onCompleted: (data) => {
        alerts.success(data.deleteAccumulationArea.archived ? "Area archived" : "Area deleted");
        navigate(`/o/${tenantId}/waste/accumulation-areas?facilityId=${area.facilityId}`);
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while archiving the area");
      },
    });

  return { archive, isDeleting };
}

/** The Volume card, which reads differently for the two kinds of area. */
function AreaVolumeCard({ area }: { area: AccumulationAreaDetailData }) {
  return (
    <SectionCard surface="paper" title="Volume">
      {area.type === WasteAccumulationAreaType.Central ? (
        <CentralVolumeSummary area={area} />
      ) : (
        <Stack spacing={1}>
          <SatelliteVolumeGauge volume={area.volume} areaName={area.name} variant="detailed" />
        </Stack>
      )}
    </SectionCard>
  );
}

function AccumulationAreaDetailContent({ area }: { area: AccumulationAreaDetailData }) {
  const { tenantId } = useTenant();
  const { archive, isDeleting } = useArchiveArea(area);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  // Containers open in a drawer over the area rather than navigating to the
  // containers list — the reader is looking at what's in this area, and the
  // answer shouldn't cost them the area.
  const [openContainerId, setOpenContainerId] = useState<string | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const isArchived = !!area.archivedAt;

  // Stable identity, or the grid's React.memo wrapper is defeated on every
  // render of this page.
  const handleRowClick = useCallback(
    (params: GridRowParams<{ id: string }>) => setOpenContainerId(params.row.id),
    []
  );

  return (
    <>
      <WastePageFrame
        header={
          <DetailHeader
            area={area}
            isArchived={isArchived}
            onEdit={() => setEditDialogOpen(true)}
            onArchive={() => setArchiveDialogOpen(true)}
          />
        }
      >
        <Stack spacing={2}>
          {isArchived && (
            <Alert severity="info" variant="outlined">
              This area was archived on {formatContainerDate(area.archivedAt)} and is read-only.
            </Alert>
          )}

          {/* Details and Volume share a row: both are short, and side by side
              they leave the containers grid the full width it needs. */}
          <Grid container spacing={2} alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <DetailsCard area={area} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <AreaVolumeCard area={area} />
            </Grid>
          </Grid>

          <SectionCard surface="paper" title="Containers">
            {tenantId && (
              // `compact`: the grid's standalone spacing assumes it IS the page,
              // so its own 24px gutters doubled the card's and left a hole under
              // the heading.
              <FacilityContainersGrid
                compact
                tenantId={tenantId}
                facilityId={area.facilityId}
                pinnedSearch={`areaId:${area.id}`}
                commandButtons={NO_COMMAND_BUTTONS}
                onRowClick={handleRowClick}
                persistenceKey="area-detail-containers-table"
                showClockTooltip
              />
            )}
          </SectionCard>
        </Stack>
      </WastePageFrame>

      <WasteContainerDrawer
        containerId={openContainerId ?? undefined}
        open={!!openContainerId}
        onClose={() => setOpenContainerId(null)}
      />

      {editDialogOpen && (
        <AccumulationAreaDialog
          open
          areaId={area.id}
          facilityId={area.facilityId}
          onClose={() => setEditDialogOpen(false)}
        />
      )}

      <ConfirmDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        header="Archive this area?"
        info="This area will be archived and its history preserved; an area that has never held containers is deleted instead."
        confirmText="Archive"
        loading={isDeleting}
        onConfirm={archive}
      />
    </>
  );
}

export function AccumulationAreaDetail() {
  const { areaId } = useParams<{ areaId: string }>();
  const { tenantId } = useTenant();

  const { data, loading, error } = useQuery(GET_ACCUMULATION_AREA_QUERY, {
    variables: { id: areaId ?? "" },
    skip: !areaId,
  });
  const area = data?.accumulationArea;

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Accumulation areas",
      to:
        tenantId && area
          ? `/o/${tenantId}/waste/accumulation-areas?facilityId=${area.facilityId}`
          : undefined,
    },
    {
      // The stored name, prefix and all — the heading is what drops it.
      label: area?.name ?? "Area",
    },
  ]);

  if (loading && !area) {
    return (
      <Stack spacing={2} sx={{ px: 3, py: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={48} />
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Skeleton variant="rectangular" width="100%" height={120} />
      </Stack>
    );
  }

  if (error || !area) {
    return (
      <Stack spacing={1} sx={{ px: 3, py: 4 }} alignItems="center">
        <Typography variant="h6">Area not found</Typography>
        <Typography variant="body2" color="text.secondary">
          The area may have been archived, or the link may be incorrect.
        </Typography>
      </Stack>
    );
  }

  return <AccumulationAreaDetailContent area={area} />;
}
